import { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Paper,
  IconButton,
  Button,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Grid,
  Alert,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, isValid, parse } from 'date-fns';
import './lease-form.scss';

// Constants
const UTILITIES_MAP = {
  electricity: 'Electricity',
  heat: 'Heat',
  water_waste: 'Water/Waste',
};

const LATE_FEE = 150.00;
const NSF_FEE = 150.00;
const PET_NON_REFUNDABLE_FEE = 350.00;

// Utility functions
const formatList = (items) => {
  if (!items?.length) return 'None';
  if (items.length === 1) return items[0];
  if (items.length === 2) return `${items[0]} and ${items[1]}`;
  return `${items.slice(0, -1).join(', ')}, and ${items[items.length - 1]}`;
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const cleanDateStr = dateString.includes('T') ? dateString.split('T')[0] : dateString;
  const date = parse(cleanDateStr, 'yyyy-MM-dd', new Date());
  if (!isValid(date)) return '-';

  try {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    const day = date.getDate();
    const month = months[date.getMonth()];
    const year = date.getFullYear();

    const getSuffix = (day) => {
      if ([1, 21, 31].includes(day)) return 'st';
      if ([2, 22].includes(day)) return 'nd';
      if ([3, 23].includes(day)) return 'rd';
      return 'th';
    };

    return `${month} ${day}${getSuffix(day)}, ${year}`;
  } catch (error) {
    console.error('Error formatting date:', error);
    return '-';
  }
};

const formatAmount = (value) => {
  if (!value) return '';
  const numValue = parseFloat(value.toString().replace(/[$,]/g, '')) || 0;
  return `$${numValue.toFixed(2)}`;
};

const generatePetContent = (propertyDetails) => {
  const { allowed_pets } = propertyDetails || {};
  const { dogs = false, cats = false } = allowed_pets || {};

  if (cats && !dogs) {
    return (
      <>
        Only Cats under 20 lbs., full grown, are allowed with a {formatAmount(PET_NON_REFUNDABLE_FEE)}, pet nonrefundable fee.
        All animals must be spayed or neutered.
        No Dogs allowed during the tenancy period.
        Violation of this can lead to eviction.
        You must pick up after your dog.
        Management will also conduct an interview with pet if needed.
        All Pets must be approved by management prior to signing the lease or during tenancy.
      </>
    );
  }

  if (cats && dogs) {
    return (
      <>
        Cats and small dogs under 20 lbs., full grown, are allowed with a {formatAmount(PET_NON_REFUNDABLE_FEE)}, pet nonrefundable fee.
        All animals must be spayed or neutered.
        Dogs must always be managed outside of your apartment while on Property.
        You must pick up after your dog.
        Management will also conduct an interview with pet if needed.
        All Pets must be approved by management prior to signing the lease or during tenancy.
      </>
    );
  }

  if (dogs && !cats) {
    return (
      <>
        Only small dogs under 20 lbs., full grown, are allowed with a {formatAmount(PET_NON_REFUNDABLE_FEE)}, pet nonrefundable fee.
        All animals must be spayed or neutered.
        No Cats allowed during the tenancy period.
        Violation of this can lead to eviction.
        Dogs must always be managed outside of your apartment while on Property.
        You must pick up after your dog.
        Management will also conduct an interview with pet if needed.
        All Pets must be approved by management prior to signing the lease or during tenancy.
      </>
    );
  }

  return "No pets are allowed during the tenancy period. Violation of this can lead to eviction.";
};

// Section components
const SectionTitle = ({ children }) => (
  <Typography variant="subtitle2" className="section-title">
    {children}
  </Typography>
);

const SectionContent = ({ children, highlighted = false, sx = {} }) => (
  <Typography
    variant="body2"
    className="section-content"
    sx={highlighted ? { backgroundColor: '#ffff99', color: 'inherit', p: 1, borderRadius: 1, ...sx } : sx}
  >
    {children}
  </Typography>
);

const HighlightedText = ({ children }) => (
  <span style={{ color: 'red', fontWeight: 'bold' }}>{children}</span>
);

const EditableDateField = ({ formik }) => {
  const [isEditingDate, setIsEditingDate] = useState(false);

  const securityDeposits = formik.values.security_deposits || {
    received_date: '',
  };

  const safeFormatDate = (val) => {
    if (!val) return '';
    const cleanDateStr = val.includes('T') ? val.split('T')[0] : val;
    const date = parse(cleanDateStr, 'yyyy-MM-dd', new Date());
    return isValid(date) ? cleanDateStr : '';
  };

  if (isEditingDate) {
    const cleanDateStr = safeFormatDate(securityDeposits.received_date);
    const parsed = cleanDateStr ? parse(cleanDateStr, 'yyyy-MM-dd', new Date()) : null;
    const dateValue = parsed && isValid(parsed) ? parsed : null;
    return (
      <Box sx={{ display: 'inline-flex', alignItems: 'center', verticalAlign: 'middle' }}>
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <DatePicker
            value={dateValue}
            onChange={(newDate) => {
              formik.setFieldValue(
                'security_deposits.received_date',
                newDate && isValid(newDate) ? format(newDate, 'yyyy-MM-dd') : ''
              );
            }}
            onClose={() => setIsEditingDate(false)}
            format="MM/dd/yyyy"
            slotProps={{
              textField: {
                size: 'small',
                autoFocus: true,
                error: !!formik.errors?.security_deposits?.received_date,
              },
            }}
          />
        </LocalizationProvider>
      </Box>
    );
  }

  return (
    <span
      style={{
        fontWeight: 600,
        color: '#1976d2',
        cursor: 'pointer',
      }}
      onClick={() => setIsEditingDate(true)}
    >
      {formatDate(securityDeposits.received_date)}
      <IconButton
        size="small"
        onClick={(e) => {
          e.stopPropagation();
          setIsEditingDate(true);
        }}
        sx={{ ml: 0.5, verticalAlign: 'middle' }}
      >
        <EditIcon fontSize="small" color="error" />
      </IconButton>
    </span>
  );
};

const SecurityDeposits = ({ formik }) => {
  const [editingField, setEditingField] = useState(null);

  const securityDeposits = formik.values.security_deposits || {
    refundable: "",
    non_refundable: "",
    received_date: ''
  };

  const handleFieldChange = (fieldName, value) => {
    formik.setFieldValue(`security_deposits.${fieldName}`, value);
  };

  const basicRent = parseFloat(formik.values.lease_details?.basic_rent || 0);
  const refundableDeposit = parseFloat(securityDeposits.refundable || 0);
  const depositExceedsRent = basicRent > 0 && refundableDeposit > basicRent;

  const securityDepositsData = [
    {
      fieldName: "refundable",
      description: "Security Deposit (Refundable)",
      amount: securityDeposits.refundable || ""
    },
    {
      fieldName: "non_refundable",
      description: "Pet Security Fee (Non-Refundable)",
      amount: securityDeposits.non_refundable || ""
    }
  ];

  const renderEditableCell = (fieldName, value) => {
    const isEditing = editingField === fieldName;

    if (isEditing) {
      return (
        <TextField
          variant="outlined"
          value={value.toString().replace('$', '')}
          onChange={(e) => handleFieldChange(fieldName, e.target.value)}
          onBlur={() => setEditingField(null)}
          onKeyDown={(e) => e.key === 'Enter' && setEditingField(null)}
          autoFocus
          size="small"
          InputProps={{
            startAdornment: <Box component="span" sx={{ pr: 1 }}>$</Box>,
          }}
        />
      );
    }

    return (
      <div
        style={{ cursor: 'pointer', padding: '8px 0' }}
        onClick={() => setEditingField(fieldName)}
      >
        {value ? formatAmount(value) : (
          <span style={{ fontStyle: 'italic', color: '#9e9e9e' }}>
            Click to edit
          </span>
        )}
        {isEditing ? (
          <Button size="small" onClick={() => setEditingField(null)}>
            Done
          </Button>
        ) : (
          <IconButton
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              setEditingField(fieldName);
            }}
          >
            <EditIcon fontSize="small" color='error' />
          </IconButton>
        )}
      </div>
    );
  };

  return (
    <Box sx={{ mt: 2 }}>
      {depositExceedsRent && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          The refundable security deposit ({formatAmount(refundableDeposit)}) is greater than the
          monthly basic rent ({formatAmount(basicRent)}). Please confirm this is intended.
        </Alert>
      )}
      <Paper variant="outlined" className="security-details-table">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Description</TableCell>
              <TableCell>Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {securityDepositsData
              .filter((deposit) => deposit.fieldName === "refundable")
              .map((deposit) => (
                <TableRow key={deposit.fieldName}>
                  <TableCell className="label">{deposit.description}</TableCell>
                  <TableCell>
                    {renderEditableCell(deposit.fieldName, deposit.amount)}
                  </TableCell>
                </TableRow>
              ))}
            {securityDepositsData
              .filter((deposit) => deposit.fieldName === "non_refundable")
              .map((deposit) => (
                <TableRow key={deposit.fieldName}>
                  <TableCell className="label">{deposit.description}</TableCell>
                  <TableCell>
                    {renderEditableCell(deposit.fieldName, deposit.amount)}
                  </TableCell>
                </TableRow>
              ))}
          </TableBody>
        </Table>
      </Paper>
    </Box>
  );
};

const EditablePetsSection = ({ formik, isEditing, setIsEditing }) => {
  const [tempAllowedPets, setTempAllowedPets] = useState(
    formik.values.property_details?.allowed_pets || {
      dogs: false,
      cats: false,
      others: false,
    }
  );
  const [validationWarning, setValidationWarning] = useState('');

  useEffect(() => {
    if (isEditing) {
      setTempAllowedPets(formik.values.property_details?.allowed_pets || {
        dogs: false,
        cats: false,
        others: false,
      });
      setValidationWarning('');
    }
  }, [isEditing, formik.values.property_details?.allowed_pets]);

  // Validate temporary state against pets array
  const validateTempAllowedPets = (tempPets) => {
    const pets = formik.values.lease_details?.pets || [];
    if (!tempPets.dogs && pets.some((pet) => pet.category?.toLowerCase().includes('dog'))) {
      return 'Dogs must be allowed when dog pets are added.';
    }
    if (!tempPets.cats && pets.some((pet) => pet.category?.toLowerCase().includes('cat'))) {
      return 'Cats must be allowed when cat pets are added.';
    }
    if (
      !tempPets.others &&
      pets.some(
        (pet) =>
          pet.category &&
          !pet.category.toLowerCase().includes('dog') &&
          !pet.category.toLowerCase().includes('cat') &&
          pet.category.trim() !== ''
      )
    ) {
      return 'Other pets must be allowed when other pet types are added.';
    }
    return '';
  };

  // Handle checkbox toggle
  const handlePetToggle = (petType) => {
    setTempAllowedPets((prev) => {
      const newState = { ...prev, [petType]: !prev[petType] };
      setValidationWarning(validateTempAllowedPets(newState));
      return newState;
    });
  };

  // Commit changes to Formik on "Done"
  const handleDone = () => {
    const warning = validateTempAllowedPets(tempAllowedPets);
    if (warning) {
      setValidationWarning(warning);
      return;
    }
    formik.setFieldValue('property_details.allowed_pets', tempAllowedPets);
    setIsEditing(false);
    setValidationWarning('');
  };

  // Cancel changes
  const handleCancel = () => {
    setTempAllowedPets(formik.values.property_details?.allowed_pets || {
      dogs: false,
      cats: false,
      others: false,
    });
    setIsEditing(false);
    setValidationWarning('');
  };

  return (
    <Box>
      {isEditing ? (
        <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="subtitle2">Edit Pet Settings</Typography>
            <Box>
              <Button
                variant="contained"
                size="small"
                onClick={handleDone}
                disabled={!!validationWarning}
                sx={{ mr: 1 }}
              >
                Done
              </Button>
              <Button
                variant="outlined"
                size="small"
                onClick={handleCancel}
              >
                Cancel
              </Button>
            </Box>
          </Box>

          {validationWarning && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              {validationWarning}
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Allowed Pets</Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempAllowedPets.cats}
                      onChange={() => handlePetToggle('cats')}
                    />
                  }
                  label="Cats (under 20 lbs., full grown)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempAllowedPets.dogs}
                      onChange={() => handlePetToggle('dogs')}
                    />
                  }
                  label="Dogs (small dogs under 20 lbs., full grown)"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={tempAllowedPets.others}
                      onChange={() => handlePetToggle('others')}
                    />
                  }
                  label="Other Pets"
                />
              </FormGroup>
            </Grid>
          </Grid>

          <Box sx={{ mt: 2, p: 2, backgroundColor: '#f5f5f5', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>Preview:</Typography>
            <Typography variant="body2">
              {generatePetContent({ ...formik.values.property_details, allowed_pets: tempAllowedPets })}
            </Typography>
          </Box>
        </Paper>
      ) : (
        <SectionContent>
          {generatePetContent(formik.values?.property_details)}
        </SectionContent>
      )}
    </Box>
  );
};

const Step2 = ({ formik }) => {
  const { values } = formik;
  const [isEditingPets, setIsEditingPets] = useState(true);

  const notIncludedUtilities = Object.entries(values?.property_details?.utilities_available || {})
    .filter(([_, isSelected]) => isSelected)
    .map(([key]) => UTILITIES_MAP[key]);

  const utilitiesString = formatList(notIncludedUtilities);

  return (
    <Box className="lease-form-step2">
      {/* Section 4: Security Deposit */}
      <Box className="section">
        <SectionTitle>4. SECURITY DEPOSIT/PET FEE DEPOSIT</SectionTitle>
        <SectionContent>
          The Landlord acknowledges receipt on <EditableDateField formik={formik} /> from the Tenant as a
          security deposit. The Security Deposit may be utilized by the Landlord in such amount
          as is necessary to provide for any or all of the following:
          <br />
          <SecurityDeposits formik={formik} />

          <Typography variant="subtitle2" className="section-bottom-content">
            A. The landlord is entitled to use and retain the security deposit in the event the tenant:
            does not take possession on the commencement of the term of this lease, does not pay the
            first installment of rent, or does not execute a lease agreement satisfactory to both parties.
          </Typography>

          <Typography variant="subtitle2" className="section-bottom-content">
            B. The landlord may apply such amounts as he/she deems necessary to provide for:
            <Box component="ol" sx={{ pl: 4 }}>
              <li>Repairing or replacing any fixtures, fittings, appliances, or facilities located in, upon or about the leased premises.</li>
              <li>Cleaning and arranging for cleaning of the leased premises; including appliances, windows, floors, walls, professionally steam cleaning the carpets, repairing paint work etc.</li>
              <li>To remedy a breach of an obligation of the tenant hereunder at any time.</li>
              <li style={{ backgroundColor: '#ffff99', color: 'inherit', borderRadius: '1px' }}>
                To cover the cost of any late rent or NSF/Returned cheque fee.
              </li>
              <li>The security deposit can NOT be used by the tenant towards any rent.</li>
            </Box>
          </Typography>
        </SectionContent>
      </Box>

      {/* Section 5: Pets */}
      <Box className="section">
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1,
          }}
        >
          <SectionTitle>5. PETS</SectionTitle>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setIsEditingPets(true)}
            startIcon={<EditIcon />}
            color="error"
          >
            Edit
          </Button>
        </Box>

        <EditablePetsSection
          formik={formik}
          isEditing={isEditingPets}
          setIsEditing={setIsEditingPets}
        />
      </Box>

      {/* Section 6: Rent Payment and Late/NSF/Eviction */}
      <Box className="section">
        <SectionTitle>6. RENT PAYMENT</SectionTitle>
        <SectionContent highlighted>
          <div className='pb-2'>
            <span className='text-black fw-bold'>Due Date:</span> Rent is due on the <HighlightedText>1st day</HighlightedText> of each month. <br />
          </div>
          <div className='pb-2'>
            <span className='text-black fw-bold'>Late Payment Fee:</span> A fee of <HighlightedText>${LATE_FEE.toFixed(2)}</HighlightedText> will be charged if the full rent is received on or after the <HighlightedText>2nd day</HighlightedText> of the month. <br />
          </div>
          <div className='pb-2'>
            <span className='text-black fw-bold'>Non-Sufficient Funds (NSF):</span> Should any payment be returned due to non-sufficient funds, the tenant agrees to pay: <br />
            <div className='ms-4'>
              <>🔸<HighlightedText>${NSF_FEE.toFixed(2)}</HighlightedText> NSF fee for each occurrence within <HighlightedText>48 hours</HighlightedText>.</> <br />
              <>🔸<HighlightedText>${LATE_FEE.toFixed(2)}</HighlightedText> late payment fee in addition within <HighlightedText>48 hours</HighlightedText>.</> <br />
            </div>
            <>Any funds received will first be applied to the outstanding balance and all Incentives can be forfeited.</> <br />
          </div>

          <div className='pb-2'>
            <span className='text-black fw-bold'>Eviction Notice:</span> If rent is not paid by the <HighlightedText>1st day</HighlightedText> of the month: <br />
            <div className='ms-4'>
              <>🔸The tenant will receive an <HighlightedText>eviction notice</HighlightedText> to vacate the property.</> <br />
              <>🔸The matter will be filed with the <HighlightedText>RTRDS</HighlightedText> unless payment arrangements have been made with management within <HighlightedText>24 hours</HighlightedText> and approved.</> <br />
            </div>
          </div>
          <div className='pb-2'>
            <><span className='text-black fw-bold'>Grounds for Eviction:</span> Any NSF occurrence within the first <HighlightedText>4 months</HighlightedText> of the lease term may constitute grounds for eviction and all Incentives can be forfeited.</><br />
          </div>
        </SectionContent>
      </Box>

      {/* Section 7: Utilities */}
      <Box className="section">
        <SectionTitle>7. UTILITIES AND SERVICE</SectionTitle>
        <SectionContent highlighted>
          <div className='pb-2'>
            The tenant agrees to pay, as when due, the charges to his premises for the following
            utilities and service for <HighlightedText>{utilitiesString}</HighlightedText>.
          </div>
          {notIncludedUtilities.length > 0 && (
            <>
              <div className='pb-2'>
                <span className='text-black fw-bold pb-2'>Utility Account Responsibility:</span> If the tenant fails to establish any required utility account, or if the landlord pays a utility on the tenant’s behalf:
                <>The tenant shall be responsible for reimbursement of the actual utility costs plus an administrative fee of <HighlightedText>$150.00</HighlightedText> per utility account not set up.</>
              </div>
              <span className='text-black fw-bold'>Consequences of Utility Non-Payment: </span> If this fee is not collected during the tenancy, it may constitute grounds for eviction. Reimbursement will be assessed during the <HighlightedText>Statement of Account (SOA)</HighlightedText> process, at which time a <HighlightedText>$300.00</HighlightedText> administrative fee per utility account not set up will apply.
            </>
          )}
        </SectionContent>
      </Box>
    </Box>
  );
};

export { Step2 };